import { ChevronRight } from "lucide-react";
import type { ModelCandidate } from "@/services/adminModelCurrency";
import CandidateRow from "./CandidateRow";
import StatusBadge from "./StatusBadge";
import { isOutOfDate } from "./compareHelpers";

interface CandidateHistoryProps {
  candidates: ModelCandidate[];
  currentModel: string;
  writable: boolean;
  onPromote: (candidateId: string) => Promise<unknown>;
  onDismiss: (candidateId: string) => Promise<unknown>;
  onRunTest: (candidateId: string) => Promise<unknown>;
}

/**
 * Results that no longer need a decision, collapsed by default: decided candidates
 * (promoted, dismissed, replaced) and ones tested against a model no longer in use.
 */
const CandidateHistory = ({ candidates, currentModel, ...actions }: CandidateHistoryProps) => {
  if (candidates.length === 0) return null;
  const outdated = candidates.filter(c => isOutOfDate(c, currentModel) && c.status !== "promoted");
  const decided = candidates.filter(c => !outdated.includes(c));
  return (
    <details className="group rounded-md border border-dashed">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="h-3.5 w-3.5 transition-transform group-open:rotate-90 motion-reduce:transition-none"
          aria-hidden="true"
        />
        Older results ({candidates.length})
      </summary>
      <div className="space-y-3 px-3 pb-3">
        {outdated.length ? (
          <ul className="space-y-2">
            {outdated.map(candidate => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                currentModel={currentModel}
                {...actions}
              />
            ))}
          </ul>
        ) : null}
        {decided.length ? (
          <ul className="space-y-1.5">
            {decided.map(candidate => (
              <li
                key={candidate.id}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
              >
                <span className="font-mono text-foreground">{candidate.model}</span>
                <StatusBadge status={candidate.status} />
                {candidate.recommendation ? (
                  <span className="min-w-0">{candidate.recommendation}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
};

export default CandidateHistory;
